// src/pages/DashboardAuditoria.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import {
  getAuditorias,
} from "../helpers/rutaAuditoria";

/* ==================================================
   FECHAS
================================================== */

const todayLocalISO = () => {
  const d = new Date();

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();

  d.setDate(1);

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

/* ==================================================
   HELPERS
================================================== */

const clean = (value) =>
  String(value ?? "").trim();

const normalizarAccion = (
  accion
) => {
  const value =
    clean(accion).toUpperCase();

  if (
    value === "ALTA" ||
    value === "CREACION"
  ) {
    return "ALTA";
  }

  if (
    value === "MODIFICACION" ||
    value === "ACTUALIZACION"
  ) {
    return "MODIFICACION";
  }

  if (
    value === "ANULACION"
  ) {
    return "ANULACION";
  }

  if (
    value === "ELIMINACION" ||
    value === "BAJA"
  ) {
    return "ELIMINACION";
  }

  return value || "OTRA";
};

const accionTexto = (
  accion
) => {
  const value =
    normalizarAccion(
      accion
    );

  if (value === "ALTA") {
    return "Alta";
  }

  if (
    value === "MODIFICACION"
  ) {
    return "Modificación";
  }

  if (
    value === "ANULACION"
  ) {
    return "Anulación";
  }

  if (
    value === "ELIMINACION"
  ) {
    return "Eliminación";
  }

  return value;
};

const accionVariant = (
  accion
) => {
  const value =
    normalizarAccion(
      accion
    );

  if (value === "ALTA") {
    return "success";
  }

  if (
    value === "MODIFICACION"
  ) {
    return "primary";
  }

  if (
    value === "ANULACION"
  ) {
    return "warning";
  }

  if (
    value === "ELIMINACION"
  ) {
    return "danger";
  }

  return "secondary";
};

const resultadoVariant = (
  resultado
) => {
  const value =
    clean(resultado)
      .toUpperCase();

  if (value === "OK") {
    return "success";
  }

  if (
    value === "ERROR"
  ) {
    return "danger";
  }

  return "secondary";
};

const rolTexto = (role) => {
  const value =
    clean(role)
      .toUpperCase();

  if (
    value === "ADMIN_ROLE"
  ) {
    return "ADMIN";
  }

  if (
    value === "USER_ROLE"
  ) {
    return "USUARIO";
  }

  if (
    value === "USER_CAM"
  ) {
    return "CAMIÓN";
  }

  if (
    value === "USER_PREV"
  ) {
    return "PREVISUALIZACIÓN";
  }

  return value || "-";
};

const formatDateTime = (
  value
) => {
  if (!value) {
    return "-";
  }

  const d =
    new Date(value);

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    return "-";
  }

  return d.toLocaleString(
    "es-AR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};

const fechaLocalKey = (
  value
) => {
  const d =
    new Date(value);

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    return "";
  }

  const year =
    d.getFullYear();

  const month =
    String(
      d.getMonth() + 1
    ).padStart(
      2,
      "0"
    );

  const day =
    String(
      d.getDate()
    ).padStart(
      2,
      "0"
    );

  return `${year}-${month}-${day}`;
};

const fechaCorta = (
  key
) => {
  if (!key) {
    return "-";
  }

  const partes =
    key.split("-");

  if (
    partes.length !== 3
  ) {
    return key;
  }

  return `${partes[2]}/${partes[1]}`;
};

const calcularResumen = (
  registros = []
) => {
  const resumen = {
    total:
      registros.length,

    altas: 0,
    modificaciones: 0,
    anulaciones: 0,
    eliminaciones: 0,
    errores: 0,
  };

  registros.forEach(
    (item) => {
      const accion =
        normalizarAccion(
          item.accion
        );

      if (
        accion === "ALTA"
      ) {
        resumen.altas += 1;
      }

      if (
        accion ===
        "MODIFICACION"
      ) {
        resumen.modificaciones +=
          1;
      }

      if (
        accion ===
        "ANULACION"
      ) {
        resumen.anulaciones +=
          1;
      }

      if (
        accion ===
        "ELIMINACION"
      ) {
        resumen.eliminaciones +=
          1;
      }

      if (
        clean(
          item.resultado
        ).toUpperCase() ===
        "ERROR"
      ) {
        resumen.errores +=
          1;
      }
    }
  );

  return resumen;
};

/* ==================================================
   COLORES
================================================== */

const COLORES_ACCIONES = {
  ALTA: "#198754",
  MODIFICACION: "#0d6efd",
  ANULACION: "#ffc107",
  ELIMINACION: "#dc3545",
};

const COLORES_PIE = [
  "#198754",
  "#0d6efd",
  "#ffc107",
  "#dc3545",
];

/* ==================================================
   COMPONENTE
================================================== */

const DashboardAuditoria =
  () => {
    const [
      desde,
      setDesde,
    ] = useState(
      firstDayMonthISO()
    );

    const [
      hasta,
      setHasta,
    ] = useState(
      todayLocalISO()
    );

    const [
      usuario,
      setUsuario,
    ] = useState("");

    const [
      modulo,
      setModulo,
    ] = useState("");

    const [
      accion,
      setAccion,
    ] = useState("");

    const [
      resultado,
      setResultado,
    ] = useState("");

    const [
      registros,
      setRegistros,
    ] = useState([]);

    const [
      usuariosDisponibles,
      setUsuariosDisponibles,
    ] = useState([]);

    const [
      modulosDisponibles,
      setModulosDisponibles,
    ] = useState([]);

    const [
      resumen,
      setResumen,
    ] = useState({
      total: 0,
      altas: 0,
      modificaciones: 0,
      anulaciones: 0,
      eliminaciones: 0,
      errores: 0,
    });

    const [
      loading,
      setLoading,
    ] = useState(false);

    const [
      error,
      setError,
    ] = useState("");

    /* ================================================
       FILTROS
    ================================================ */

    const obtenerFiltrosActuales =
      (
        reemplazos = {}
      ) => ({
        desde:
          reemplazos.desde !==
          undefined
            ? reemplazos.desde
            : desde,

        hasta:
          reemplazos.hasta !==
          undefined
            ? reemplazos.hasta
            : hasta,

        usuario:
          reemplazos.usuario !==
          undefined
            ? reemplazos.usuario
            : usuario,

        modulo:
          reemplazos.modulo !==
          undefined
            ? reemplazos.modulo
            : clean(
                modulo
              ).toUpperCase(),

        accion:
          reemplazos.accion !==
          undefined
            ? reemplazos.accion
            : clean(
                accion
              ).toUpperCase(),

        resultado:
          reemplazos.resultado !==
          undefined
            ? reemplazos.resultado
            : clean(
                resultado
              ).toUpperCase(),
      });

    /* ================================================
       CARGAR DASHBOARD
    ================================================ */

    const cargarDashboard =
      async (
        filtrosPersonalizados =
          null
      ) => {
        setLoading(true);
        setError("");

        try {
          const filtros =
            filtrosPersonalizados ||
            obtenerFiltrosActuales();

          /*
            Pedimos primero 200.
          */
          const primeraRespuesta =
            await getAuditorias({
              page: 1,
              limit: 200,
              ...filtros,
            });

          let todos =
            Array.isArray(
              primeraRespuesta
                ?.auditorias
            )
              ? primeraRespuesta
                  .auditorias
              : [];

          const totalRegistros =
            Number(
              primeraRespuesta
                ?.total ||
                todos.length ||
                0
            );

          /*
            Si hay más de 200,
            seguimos trayendo páginas.
          */
          let paginaActual =
            2;

          while (
            todos.length <
            totalRegistros
          ) {
            const siguienteRespuesta =
              await getAuditorias({
                page:
                  paginaActual,

                limit: 200,

                ...filtros,
              });

            const lote =
              Array.isArray(
                siguienteRespuesta
                  ?.auditorias
              )
                ? siguienteRespuesta
                    .auditorias
                : [];

            if (
              lote.length ===
              0
            ) {
              break;
            }

            todos =
              todos.concat(
                lote
              );

            paginaActual +=
              1;
          }

          setRegistros(
            todos
          );

          const resumenBackend =
            primeraRespuesta
              ?.resumen;

          const resumenCalculado =
            calcularResumen(
              todos
            );

          setResumen({
            total: Number(
              resumenBackend
                ?.total ??
                totalRegistros ??
                resumenCalculado.total
            ),

            altas: Number(
              resumenBackend
                ?.altas ??
                resumenCalculado.altas
            ),

            modificaciones:
              Number(
                resumenBackend
                  ?.modificaciones ??
                  resumenCalculado.modificaciones
              ),

            anulaciones:
              Number(
                resumenBackend
                  ?.anulaciones ??
                  resumenCalculado.anulaciones
              ),

            eliminaciones:
              Number(
                resumenBackend
                  ?.eliminaciones ??
                  resumenCalculado.eliminaciones
              ),

            errores: Number(
              resumenBackend
                ?.errores ??
                resumenCalculado.errores
            ),
          });

          setUsuariosDisponibles(
            Array.isArray(
              primeraRespuesta
                ?.usuarios
            )
              ? primeraRespuesta
                  .usuarios
              : []
          );

          const nuevosModulos =
            Array.from(
              new Set(
                todos
                  .map(
                    (item) =>
                      clean(
                        item.modulo
                      )
                  )
                  .filter(
                    Boolean
                  )
              )
            );

          setModulosDisponibles(
            nuevosModulos.sort(
              (a, b) =>
                a.localeCompare(
                  b,
                  "es"
                )
            )
          );
        } catch (err) {
          console.error(
            "Error cargando dashboard de auditoría:",
            err
          );

          setError(
            err?.response?.data
              ?.error ||
              err?.response?.data
                ?.message ||
              err?.message ||
              "No se pudo cargar el dashboard."
          );

          setRegistros(
            []
          );

          setResumen({
            total: 0,
            altas: 0,
            modificaciones: 0,
            anulaciones: 0,
            eliminaciones: 0,
            errores: 0,
          });
        } finally {
          setLoading(
            false
          );
        }
      };

    useEffect(() => {
      cargarDashboard();

      // eslint-disable-next-line
    }, []);

    /* ================================================
       ACTIVIDAD POR DÍA
    ================================================ */

    const actividadPorDia =
      useMemo(() => {
        const mapa =
          new Map();

        registros.forEach(
          (item) => {
            const key =
              fechaLocalKey(
                item.fecha
              );

            if (!key) {
              return;
            }

            if (
              !mapa.has(
                key
              )
            ) {
              mapa.set(
                key,
                {
                  fechaKey:
                    key,

                  fecha:
                    fechaCorta(
                      key
                    ),

                  total: 0,

                  altas: 0,

                  modificaciones:
                    0,

                  anulaciones:
                    0,

                  eliminaciones:
                    0,

                  errores: 0,
                }
              );
            }

            const punto =
              mapa.get(
                key
              );

            punto.total +=
              1;

            const tipo =
              normalizarAccion(
                item.accion
              );

            if (
              tipo ===
              "ALTA"
            ) {
              punto.altas +=
                1;
            }

            if (
              tipo ===
              "MODIFICACION"
            ) {
              punto.modificaciones +=
                1;
            }

            if (
              tipo ===
              "ANULACION"
            ) {
              punto.anulaciones +=
                1;
            }

            if (
              tipo ===
              "ELIMINACION"
            ) {
              punto.eliminaciones +=
                1;
            }

            if (
              clean(
                item.resultado
              ).toUpperCase() ===
              "ERROR"
            ) {
              punto.errores +=
                1;
            }
          }
        );

        return Array.from(
          mapa.values()
        ).sort(
          (a, b) =>
            a.fechaKey.localeCompare(
              b.fechaKey
            )
        );
      }, [
        registros,
      ]);

    /* ================================================
       ACTIVIDAD POR USUARIO
    ================================================ */

    const actividadPorUsuario =
      useMemo(() => {
        const mapa =
          new Map();

        registros.forEach(
          (item) => {
            const nombre =
              item.usuarioNombre ||
              item.usuarioEmail ||
              "Sin usuario";

            const key =
              item.usuario ||
              item.usuarioEmail ||
              nombre;

            if (
              !mapa.has(
                key
              )
            ) {
              mapa.set(
                key,
                {
                  usuario:
                    nombre,

                  email:
                    item.usuarioEmail ||
                    "",

                  rol:
                    rolTexto(
                      item.usuarioRole
                    ),

                  movimientos:
                    0,

                  altas: 0,

                  modificaciones:
                    0,

                  anulaciones:
                    0,

                  eliminaciones:
                    0,
                }
              );
            }

            const fila =
              mapa.get(
                key
              );

            fila.movimientos +=
              1;

            const tipo =
              normalizarAccion(
                item.accion
              );

            if (
              tipo ===
              "ALTA"
            ) {
              fila.altas +=
                1;
            }

            if (
              tipo ===
              "MODIFICACION"
            ) {
              fila.modificaciones +=
                1;
            }

            if (
              tipo ===
              "ANULACION"
            ) {
              fila.anulaciones +=
                1;
            }

            if (
              tipo ===
              "ELIMINACION"
            ) {
              fila.eliminaciones +=
                1;
            }
          }
        );

        return Array.from(
          mapa.values()
        )
          .sort(
            (a, b) =>
              b.movimientos -
              a.movimientos
          )
          .slice(
            0,
            10
          );
      }, [
        registros,
      ]);

    /* ================================================
       ACTIVIDAD POR MÓDULO
    ================================================ */

    const actividadPorModulo =
      useMemo(() => {
        const mapa =
          new Map();

        registros.forEach(
          (item) => {
            const nombre =
              item.modulo ||
              "SIN MÓDULO";

            mapa.set(
              nombre,

              Number(
                mapa.get(
                  nombre
                ) || 0
              ) + 1
            );
          }
        );

        return Array.from(
          mapa.entries()
        )
          .map(
            ([
              nombre,
              movimientos,
            ]) => ({
              modulo:
                nombre,

              movimientos,
            })
          )
          .sort(
            (a, b) =>
              b.movimientos -
              a.movimientos
          )
          .slice(
            0,
            10
          );
      }, [
        registros,
      ]);

    /* ================================================
       DISTRIBUCIÓN ACCIONES
    ================================================ */

    const distribucionAcciones =
      useMemo(() => {
        const datos = [
          {
            name:
              "Altas",
            value:
              resumen.altas,
          },

          {
            name:
              "Modificaciones",

            value:
              resumen.modificaciones,
          },

          {
            name:
              "Anulaciones",

            value:
              resumen.anulaciones,
          },

          {
            name:
              "Eliminaciones",

            value:
              resumen.eliminaciones,
          },
        ];

        return datos.filter(
          (item) =>
            Number(
              item.value ||
                0
            ) > 0
        );
      }, [
        resumen,
      ]);

    /* ================================================
       ÚLTIMOS MOVIMIENTOS
    ================================================ */

    const ultimosMovimientos =
      useMemo(() => {
        return [
          ...registros,
        ]
          .sort(
            (a, b) => {
              const fechaA =
                new Date(
                  a.fecha ||
                    0
                ).getTime();

              const fechaB =
                new Date(
                  b.fecha ||
                    0
                ).getTime();

              return (
                fechaB -
                fechaA
              );
            }
          )
          .slice(
            0,
            10
          );
      }, [
        registros,
      ]);

    const usuarioMasActivo =
      actividadPorUsuario
        .length > 0
        ? actividadPorUsuario[0]
        : null;

    const moduloMasActivo =
      actividadPorModulo
        .length > 0
        ? actividadPorModulo[0]
        : null;

    /* ================================================
       LIMPIAR
    ================================================ */

    const limpiarFiltros =
      () => {
        const filtrosLimpios =
          {
            desde:
              firstDayMonthISO(),

            hasta:
              todayLocalISO(),

            usuario: "",
            modulo: "",
            accion: "",
            resultado: "",
          };

        setDesde(
          filtrosLimpios.desde
        );

        setHasta(
          filtrosLimpios.hasta
        );

        setUsuario("");
        setModulo("");
        setAccion("");
        setResultado("");

        cargarDashboard(
          filtrosLimpios
        );
      };

    /* ================================================
       RENDER
    ================================================ */

    return (
      <div className="container-fluid mt-4 mb-4">
        {/* CABECERA */}

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-0">
              Dashboard de Auditoría
            </h4>

            <small className="text-muted">
              Resumen general de la actividad realizada en DANLU
            </small>
          </div>

          <div className="mt-2">
            <Button
              variant="outline-secondary"
              size="sm"
              className="mr-2"
              onClick={
                limpiarFiltros
              }
              disabled={
                loading
              }
            >
              Limpiar
            </Button>

            <Button
              variant="dark"
              size="sm"
              onClick={() =>
                cargarDashboard()
              }
              disabled={
                loading
              }
            >
              {loading
                ? "Buscando..."
                : "Buscar"}
            </Button>
          </div>
        </div>

        {/* FILTROS */}

        <Card className="mb-3">
          <Card.Body>
            <Row>
              <Col
                xs={12}
                md={3}
                lg={2}
                className="mb-2"
              >
                <Form.Label>
                  Desde
                </Form.Label>

                <Form.Control
                  type="date"
                  size="sm"
                  value={
                    desde
                  }
                  onChange={(
                    e
                  ) =>
                    setDesde(
                      e.target
                        .value
                    )
                  }
                />
              </Col>

              <Col
                xs={12}
                md={3}
                lg={2}
                className="mb-2"
              >
                <Form.Label>
                  Hasta
                </Form.Label>

                <Form.Control
                  type="date"
                  size="sm"
                  value={
                    hasta
                  }
                  onChange={(
                    e
                  ) =>
                    setHasta(
                      e.target
                        .value
                    )
                  }
                />
              </Col>

              <Col
                xs={12}
                md={6}
                lg={3}
                className="mb-2"
              >
                <Form.Label>
                  Usuario
                </Form.Label>

                <Form.Control
                  as="select"
                  size="sm"
                  value={
                    usuario
                  }
                  onChange={(
                    e
                  ) =>
                    setUsuario(
                      e.target
                        .value
                    )
                  }
                >
                  <option value="">
                    Todos los usuarios
                  </option>

                  {usuariosDisponibles.map(
                    (
                      item
                    ) => (
                      <option
                        key={
                          item.id
                        }
                        value={
                          item.id
                        }
                      >
                        {item.usuarioNombre ||
                          item.usuarioEmail ||
                          "Usuario"}
                        {" - "}
                        {rolTexto(
                          item.usuarioRole
                        )}
                      </option>
                    )
                  )}
                </Form.Control>
              </Col>

              <Col
                xs={12}
                md={4}
                lg={2}
                className="mb-2"
              >
                <Form.Label>
                  Módulo
                </Form.Label>

                <Form.Control
                  as="select"
                  size="sm"
                  value={
                    modulo
                  }
                  onChange={(
                    e
                  ) =>
                    setModulo(
                      e.target
                        .value
                    )
                  }
                >
                  <option value="">
                    Todos
                  </option>

                  {modulosDisponibles.map(
                    (
                      item
                    ) => (
                      <option
                        key={
                          item
                        }
                        value={
                          item
                        }
                      >
                        {
                          item
                        }
                      </option>
                    )
                  )}
                </Form.Control>
              </Col>

              <Col
                xs={12}
                md={4}
                lg={2}
                className="mb-2"
              >
                <Form.Label>
                  Acción
                </Form.Label>

                <Form.Control
                  as="select"
                  size="sm"
                  value={
                    accion
                  }
                  onChange={(
                    e
                  ) =>
                    setAccion(
                      e.target
                        .value
                    )
                  }
                >
                  <option value="">
                    Todas
                  </option>

                  <option value="ALTA">
                    ALTA
                  </option>

                  <option value="MODIFICACION">
                    MODIFICACIÓN
                  </option>

                  <option value="ANULACION">
                    ANULACIÓN
                  </option>

                  <option value="ELIMINACION">
                    ELIMINACIÓN
                  </option>
                </Form.Control>
              </Col>

              <Col
                xs={12}
                md={4}
                lg={1}
                className="mb-2"
              >
                <Form.Label>
                  Resultado
                </Form.Label>

                <Form.Control
                  as="select"
                  size="sm"
                  value={
                    resultado
                  }
                  onChange={(
                    e
                  ) =>
                    setResultado(
                      e.target
                        .value
                    )
                  }
                >
                  <option value="">
                    Todos
                  </option>

                  <option value="OK">
                    OK
                  </option>

                  <option value="ERROR">
                    ERROR
                  </option>
                </Form.Control>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && (
          <div className="alert alert-danger py-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />

            <div className="mt-2">
              Cargando dashboard...
            </div>
          </div>
        ) : (
          <>
            {/* KPIs */}

            <Row className="mb-3">
              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Movimientos
                    </small>

                    <h4 className="mb-0">
                      {
                        resumen.total
                      }
                    </h4>

                    <small className="text-muted">
                      Total registrado
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Altas
                    </small>

                    <h4 className="mb-0 text-success">
                      {
                        resumen.altas
                      }
                    </h4>

                    <small className="text-muted">
                      Nuevos registros
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Modificaciones
                    </small>

                    <h4 className="mb-0 text-primary">
                      {
                        resumen.modificaciones
                      }
                    </h4>

                    <small className="text-muted">
                      Datos modificados
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Anulaciones
                    </small>

                    <h4 className="mb-0 text-warning">
                      {
                        resumen.anulaciones
                      }
                    </h4>

                    <small className="text-muted">
                      Registros anulados
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Eliminaciones
                    </small>

                    <h4 className="mb-0 text-danger">
                      {
                        resumen.eliminaciones
                      }
                    </h4>

                    <small className="text-muted">
                      Bajas registradas
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Errores
                    </small>

                    <h4 className="mb-0 text-danger">
                      {
                        resumen.errores
                      }
                    </h4>

                    <small className="text-muted">
                      Operaciones fallidas
                    </small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* GRÁFICOS PRINCIPALES */}

            <Row className="mb-3">
              <Col
                lg={8}
                className="mb-3"
              >
                <Card className="h-100">
                  <Card.Body>
                    <h6 className="mb-3">
                      Actividad por día
                    </h6>

                    <div
                      style={{
                        width:
                          "100%",
                        height:
                          320,
                      }}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <BarChart
                          data={
                            actividadPorDia
                          }
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                          />

                          <XAxis
                            dataKey="fecha"
                            tick={{
                              fontSize:
                                11,
                            }}
                          />

                          <YAxis
                            allowDecimals={
                              false
                            }
                          />

                          <Tooltip />

                          <Legend />

                          <Bar
                            dataKey="altas"
                            name="Altas"
                            stackId="acciones"
                            fill={
                              COLORES_ACCIONES.ALTA
                            }
                          />

                          <Bar
                            dataKey="modificaciones"
                            name="Modificaciones"
                            stackId="acciones"
                            fill={
                              COLORES_ACCIONES.MODIFICACION
                            }
                          />

                          <Bar
                            dataKey="anulaciones"
                            name="Anulaciones"
                            stackId="acciones"
                            fill={
                              COLORES_ACCIONES.ANULACION
                            }
                          />

                          <Bar
                            dataKey="eliminaciones"
                            name="Eliminaciones"
                            stackId="acciones"
                            fill={
                              COLORES_ACCIONES.ELIMINACION
                            }
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                lg={4}
                className="mb-3"
              >
                <Card className="h-100">
                  <Card.Body>
                    <h6 className="mb-3">
                      Distribución por acción
                    </h6>

                    <div
                      style={{
                        width:
                          "100%",
                        height:
                          320,
                      }}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <PieChart>
                          <Pie
                            data={
                              distribucionAcciones
                            }
                            dataKey="value"
                            nameKey="name"
                            innerRadius={
                              60
                            }
                            outerRadius={
                              100
                            }
                            paddingAngle={
                              2
                            }
                            label
                          >
                            {distribucionAcciones.map(
                              (
                                item,
                                index
                              ) => (
                                <Cell
                                  key={`${item.name}-${index}`}
                                  fill={
                                    COLORES_PIE[
                                      index %
                                        COLORES_PIE.length
                                    ]
                                  }
                                />
                              )
                            )}
                          </Pie>

                          <Tooltip />

                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* USUARIO / MODULO */}

            <Row className="mb-3">
              <Col
                lg={6}
                className="mb-3"
              >
                <Card className="h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        Actividad por usuario
                      </h6>

                      {usuarioMasActivo && (
                        <small className="text-muted">
                          Más activo:{" "}
                          <strong>
                            {
                              usuarioMasActivo.usuario
                            }
                          </strong>
                        </small>
                      )}
                    </div>

                    <div
                      style={{
                        width:
                          "100%",
                        height:
                          320,
                      }}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <BarChart
                          data={
                            actividadPorUsuario
                          }
                          layout="vertical"
                          margin={{
                            left:
                              20,
                            right:
                              20,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                          />

                          <XAxis
                            type="number"
                            allowDecimals={
                              false
                            }
                          />

                          <YAxis
                            type="category"
                            dataKey="usuario"
                            width={
                              115
                            }
                            tick={{
                              fontSize:
                                10,
                            }}
                          />

                          <Tooltip />

                          <Legend />

                          <Bar
                            dataKey="movimientos"
                            name="Movimientos"
                            fill="#0d6efd"
                          />

                          <Bar
                            dataKey="altas"
                            name="Altas"
                            fill="#198754"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                lg={6}
                className="mb-3"
              >
                <Card className="h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        Actividad por módulo
                      </h6>

                      {moduloMasActivo && (
                        <small className="text-muted">
                          Más activo:{" "}
                          <strong>
                            {
                              moduloMasActivo.modulo
                            }
                          </strong>
                        </small>
                      )}
                    </div>

                    <div
                      style={{
                        width:
                          "100%",
                        height:
                          320,
                      }}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <BarChart
                          data={
                            actividadPorModulo
                          }
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                          />

                          <XAxis
                            dataKey="modulo"
                            tick={{
                              fontSize:
                                10,
                            }}
                          />

                          <YAxis
                            allowDecimals={
                              false
                            }
                          />

                          <Tooltip />

                          <Bar
                            dataKey="movimientos"
                            name="Movimientos"
                            fill="#6f42c1"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* TABLAS */}

            <Row>
              <Col
                lg={5}
                className="mb-3"
              >
                <Card className="h-100">
                  <Card.Body>
                    <h6 className="mb-3">
                      Resumen por usuario
                    </h6>

                    <div className="table-responsive">
                      <Table
                        striped
                        hover
                        size="sm"
                      >
                        <thead>
                          <tr>
                            <th>
                              Usuario
                            </th>

                            <th className="text-right">
                              Mov.
                            </th>

                            <th className="text-right text-success">
                              Altas
                            </th>

                            <th className="text-right text-primary">
                              Modif.
                            </th>

                            <th className="text-right text-danger">
                              Elim.
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {actividadPorUsuario.length ===
                          0 ? (
                            <tr>
                              <td
                                colSpan="5"
                                className="text-center text-muted py-3"
                              >
                                Sin movimientos.
                              </td>
                            </tr>
                          ) : (
                            actividadPorUsuario.map(
                              (
                                item
                              ) => (
                                <tr
                                  key={`${item.usuario}-${item.email}`}
                                >
                                  <td>
                                    <div className="font-weight-bold">
                                      {
                                        item.usuario
                                      }
                                    </div>

                                    <small className="text-muted">
                                      {
                                        item.rol
                                      }
                                    </small>
                                  </td>

                                  <td className="text-right font-weight-bold">
                                    {
                                      item.movimientos
                                    }
                                  </td>

                                  <td className="text-right text-success">
                                    {
                                      item.altas
                                    }
                                  </td>

                                  <td className="text-right text-primary">
                                    {
                                      item.modificaciones
                                    }
                                  </td>

                                  <td className="text-right text-danger">
                                    {
                                      item.eliminaciones
                                    }
                                  </td>
                                </tr>
                              )
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                lg={7}
                className="mb-3"
              >
                <Card className="h-100">
                  <Card.Body>
                    <h6 className="mb-3">
                      Últimos movimientos auditados
                    </h6>

                    <div className="table-responsive">
                      <Table
                        striped
                        hover
                        size="sm"
                      >
                        <thead>
                          <tr>
                            <th>
                              Fecha
                            </th>

                            <th>
                              Usuario
                            </th>

                            <th>
                              Módulo
                            </th>

                            <th>
                              Acción
                            </th>

                            <th>
                              Referencia
                            </th>

                            <th>
                              Resultado
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {ultimosMovimientos.length ===
                          0 ? (
                            <tr>
                              <td
                                colSpan="6"
                                className="text-center text-muted py-3"
                              >
                                Sin movimientos.
                              </td>
                            </tr>
                          ) : (
                            ultimosMovimientos.map(
                              (
                                item
                              ) => (
                                <tr
                                  key={
                                    item._id
                                  }
                                >
                                  <td
                                    style={{
                                      whiteSpace:
                                        "nowrap",
                                    }}
                                  >
                                    {formatDateTime(
                                      item.fecha
                                    )}
                                  </td>

                                  <td>
                                    {item.usuarioNombre ||
                                      item.usuarioEmail ||
                                      "-"}
                                  </td>

                                  <td className="font-weight-bold">
                                    {item.modulo ||
                                      "-"}
                                  </td>

                                  <td>
                                    <Badge
                                      variant={accionVariant(
                                        item.accion
                                      )}
                                    >
                                      {accionTexto(
                                        item.accion
                                      )}
                                    </Badge>
                                  </td>

                                  <td>
                                    {item.referencia ||
                                      "-"}
                                  </td>

                                  <td>
                                    <Badge
                                      variant={resultadoVariant(
                                        item.resultado
                                      )}
                                    >
                                      {item.resultado ||
                                        "-"}
                                    </Badge>
                                  </td>
                                </tr>
                              )
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    );
  };

export default DashboardAuditoria;